import { createContext, Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';
import { ChainId, NATIVE_ADDRESS, NATIVE_SOL, WRAPPED_SOL, ZERO_ADDRESS } from '@shared/utils/config/networks';
import { MultichainSwapResult, OrderType } from '@shared/types/actions';
import { Address, CovalentBalanceItem, IExecuteViaClientResponse } from '@shared/types/frontend';
import { slackLogger } from '@shared/utils/off-chain/slack-logger';
import { TQuoteData } from '@shared/utils/transactions/genius-swap/types';
import { fetchSVMTokenBalances } from '@shared/utils/solana/base/fetchSVMTokenBalances';
import { fetchEVMTokenBalances } from '@shared/utils/evm/fetchEVMTokenBalance';
import { useLocalStorage } from 'usehooks-ts';
import { useRouter } from 'next/router';
import { ENVIRONMENT } from '@shared/utils/config/environment';
import { toast } from 'sonner';
import { WrappedNativeTokens } from '@shared/types/quoting-engine/wrappedNativeTokens';
import { getTokenUSDPrice } from '@shared/utils/priceFeeds/getTokenUSDPrice';
import { V2Networks } from '@shared/utils/config/networkAddresses';
import updateIndexerAssets from '@/lib/trade/updateIndexerAssets';
import { delay } from '@/hooks/LitSessionContext/useLitAuth';
import awaitTransactionConfirmation from '@/lib/trade/awaitTransactionConfirmation';
import { useInterval } from '@/hooks/utilities/useInterval';
import getFromAssetPrices from '@/lib/trade/getFromAssetPrices';
import getToAssetPrices from '@/lib/trade/getToAssetPrices';
import useGetWrappedWallet from '@/hooks/auth/useGetWrappedWallet';
import { useMixpanel } from '@/lib/misc/mixpanel';
import { useIsMobile } from '@/hooks/utilities/use-mobile';
import { executeOrderWrapper } from '@/lib/executorHelpers/executeOrder/executeOrderWrapper';
import { IEnginePriceParams, useEnginePrice } from '@/hooks/QuotingEngineCalls/price/useEnginePrice';
import buildAllPermit2Details from '@/lib/trade/buildAllPermit2Details';
import { toSpecialFixed } from '@/lib/misc/utils';
import { QuoteStringGeneric, QuoteStringResponse, quoteString, validate } from '@/components/trade/swap-home/const';
import { useSession } from './LitSessionProvider';
import useSpecialFixDecimals, { ISpecialFixDecimals } from '@/hooks/utilities/useSpecialFixDecimals';
import { TBasicTokenInfo } from '@/hooks/pricing/useBasicTokenInfo';
type TabType = 'swap' | 'buy' | 'sell';

// TODO: find error format

interface SwapContextInterface {
  selectFromAsset: boolean;
  setSelectFromAsset: (value: boolean) => void;
  selectToAsset: boolean;
  setSelectToAsset: (value: boolean) => void;
  toAssets: CovalentBalanceItem[];
  setToAssets: Dispatch<SetStateAction<CovalentBalanceItem[]>>;
  fromAssets: CovalentBalanceItem[];
  setFromAssets: Dispatch<SetStateAction<CovalentBalanceItem[]>>;
  timeUntilRefetchQuote: number;
  setTimeUntilRefetchQuote: (value: number) => void;
  swapLoading: boolean;
  setSwapLoading: (value: boolean) => void;
  swapResult: MultichainSwapResult | {
    error: string;
  };
  setSwapResult: (value: MultichainSwapResult | {
    error: string;
  }) => void;
  amount: string;
  setAmount: (value: string) => void;
  specialFixDecimals: ISpecialFixDecimals[];
  quoteData: TQuoteData;
  quoteLoad: boolean;
  quoteFetching: boolean;
  toAssetPriceData: TBasicTokenInfo[];
  activeTab: TabType;
  setActiveTab: (value: TabType) => void;
  quoteStringInfo: QuoteStringGeneric<QuoteStringResponse>;
  swapWrapper: () => void;
  fromConfig: {
    gas: number;
    slippage: number;
  };
  setFromConfig: (value: {
    gas: number;
    slippage: number;
  }) => void;
  toConfig: {
    gas: number;
    slippage: number;
  };
  setToConfig: (value: {
    gas: number;
    slippage: number;
  }) => void;
  priceFetching: boolean;
  isConfirmationDialogOpen: boolean;
  setIsConfirmationDialogOpen: (value: boolean) => void;
  quoteRefetch: () => void;
  setPriceFetching: (value: boolean) => void;
  resetAssets: () => void;
  triggerQuote: () => void;
  handleOnRefresh: () => Promise<void>;
}
const SwapContext = createContext({} as SwapContextInterface);
export const SwapProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    PKP,
    litSession
  } = useSession();
  const {
    getEVMWallet,
    getSolanaWallet
  } = useGetWrappedWallet();
  const [selectFromAsset, setSelectFromAsset] = useState(false);
  const [selectToAsset, setSelectToAsset] = useState(false);
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [fromAssets, setFromAssets] = useState<CovalentBalanceItem[]>([]);
  const [toAssets, setToAssets] = useState<CovalentBalanceItem[]>([]);
  const fromAssetsFromState = fromAssets;
  const toAssetsFromState = toAssets;
  const [timeUntilRefetchQuote, setTimeUntilRefetchQuote] = useState(10);
  const [swapLoading, setSwapLoading] = useState(false);
  const isMobile = useIsMobile();
  const router = useRouter();
  const urlHasTrade = router.asPath?.includes('trade');
  const urlHasTradeRef = useRef(false);
  useEffect(() => {
    if (urlHasTrade) {
      urlHasTradeRef.current = true;
    }
    if (urlHasTradeRef.current && !urlHasTrade) {
      // Navigated off trade page
      resetAssets();
    }
  }, [router]);
  const queryClient = new QueryClient();
  const {
    data: toAssetPriceData
  } = useQuery({
    queryKey: ['toAssetPriceData', toAssets],
    queryFn: async () => {
      return getToAssetPrices(toAssets);
    },
    enabled: toAssets.length > 0,
    refetchInterval: 5000
  });
  const [swapResult, setSwapResult] = useState<MultichainSwapResult | {
    error: string;
    time?: number;
  }>({
    error: '',
    time: 0
  });
  const [activeTab, setActiveTab] = useState<TabType>('swap');
  const [amount, setAmount] = useState('0');
  const {
    specialFixDecimals
  } = useSpecialFixDecimals({
    assets: fromAssets
  });
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [priceFetching, setPriceFetching] = useState<boolean>(false);
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const [startTransactionConfirmationCheck, setStartTransactionConfirmationCheck] = useLocalStorage<{
    tokenName?: string;
    hash: string;
    network: number;
    bundleId?: string;
  }[]>('startTransactionConfirmationCheck', []);
  const [fromConfig, setFromConfig] = useLocalStorage<{
    gas: number;
    slippage: number;
  }>('swap-from-config', {
    gas: 1,
    slippage: 1
  });
  const [toConfig, setToConfig] = useLocalStorage<{
    gas: number;
    slippage: number;
  }>('swap-to-config', {
    gas: 1,
    slippage: 1
  });
  useEffect(() => {
    async function beginCheck() {
      const id = toast.loading(`${startTransactionConfirmationCheck?.[0]?.tokenName} Order Finalizing...`, {
        dismissible: true
      });
      try {
        // const jitoBundleId = startTransactionConfirmationCheck?.[0]?.bundleId;

        // if (jitoBundleId) {
        //   const jitoResponse = await checkJitoBundleStatus(jitoBundleId);
        //   if (jitoResponse == 'Failed') {
        //     throw new Error('Jito bundle failed');
        //   }
        // }

        const results = await awaitTransactionConfirmation({
          hashPayload: startTransactionConfirmationCheck
        });
        if ((results as any)?.error) {
          toast.error(`Your ${startTransactionConfirmationCheck?.[0]?.tokenName} order may not have landed on chain. Please check your transaction history and try again.`, {
            id,
            dismissible: true
          });
          await delay(8000);
          toast.dismiss(id);
        } else {
          toast.success(`${startTransactionConfirmationCheck?.[0]?.tokenName} Order Confirmed! Your balances may take 5-10 seconds to update.`, {
            id
          });
          LogToMixpanel({
            pkp: PKP,
            eventName: EVENTS.trade.finalizeTrade,
            category: CATEGORIES.trade,
            metaData: {
              fromAssets: fromAssets,
              toAssets: toAssets,
              amount: amount,
              results
            }
          });
          await delay(1500);
          toast.dismiss(id);
        }
      } catch (e) {
        console.error('beginCheck error', e);
        toast.error(`${startTransactionConfirmationCheck?.[0]?.tokenName} Order failed to finalize. Please try again.`, {
          id
        });
        await delay(3000);
        toast.dismiss(id);
      } finally {
        toast.dismiss(id);
        setStartTransactionConfirmationCheck([]);
        updateIndexerAssets({
          fromAssets,
          toAssets,
          PKP,
          queryClient
        });
      }
    }
    if (startTransactionConfirmationCheck.length > 0) {
      console.log('Executing order - beginCheck', startTransactionConfirmationCheck);
      beginCheck();
    }
  }, [startTransactionConfirmationCheck]);
  const {
    data: quoteData,
    isLoading: quoteLoad,
    isFetching: quoteFetching,
    refetch: quoteRefetch,
    setParams
  } = useEnginePrice();
  const clearQuoteData = () => {
    queryClient.invalidateQueries({
      queryKey: ['enginePrice']
    });
  };
  useEffect(() => {
    if (!quoteLoad && !quoteFetching) {
      setPriceFetching(false);
    }
  }, [quoteLoad, quoteFetching]);
  useEffect(() => {
    const usdAmount = fromAssets?.reduce((acc, curr) => {
      const usdAmount = curr.amountToUse;
      return acc + usdAmount;
    }, 0);
    setAmount(toSpecialFixed(usdAmount, 7).value);
  }, [fromAssets]);
  async function triggerQuote() {
    setPriceFetching(true);
    if (!validate(fromAssetsFromState, toAssetsFromState).valid) {
      setPriceFetching(false);
      console.log('New quote triggered from assets not valid', fromAssetsFromState, toAssetsFromState, validate(fromAssetsFromState, toAssetsFromState));
      return;
    }
    const fromAssets = await getFromAssetPrices({
      fromAssets: fromAssetsFromState
    });
    const toAssets = toAssetsFromState.map(x => {
      const isWrappedAddress = x.contract_address?.toLowerCase() == WrappedNativeTokens?.get(x?.network)?.toLowerCase();
      const newAddress = isWrappedAddress ? ZERO_ADDRESS : x.contract_address;
      return {
        ...x,
        contract_address: newAddress
      };
    });
    const isNativeSwap = fromAssets?.[0]?.contract_address == 'So11111111111111111111111111111111111111112' && fromAssets?.[0]?.contract_name == 'Solana';
    if (isNativeSwap) {
      fromAssets[0].contract_address = NATIVE_SOL;
    }
    console.log('New quote triggered from assets', fromAssets, fromAssetsFromState);
    const tokensIn = await Promise.all(fromAssets.map(async x => {
      if (isNaN(x.amountToUse) && x.amountToUseStr) {
        x.amountToUse = parseFloat(x.amountToUseStr);
      }
      const percentage = x.amountToUse / x.usd_value;
      let amount;
      if (typeof x.amountToUse === 'number' && typeof x.formatted_balance === 'number' && x.formatted_balance !== 0) {
        const calculatedAmount = x.amountToUse / x.quote_rate / x.formatted_balance * parseFloat(x.balance);
        if (!isNaN(calculatedAmount)) {
          amount = BigInt(Math.round(calculatedAmount))?.toString();
        } else {
          console.error('Calculated amount is NaN:', {
            x,
            calculatedAmount
          });
          amount = '0';
        }
      } else {
        console.error('Invalid input for amount calculation:', x);
        amount = '0';
      }
      if (percentage > 0.98) {
        let evmSVMBalances;
        if (x.network == ChainId.SOLANA) {
          evmSVMBalances = await fetchSVMTokenBalances({
            network: x.network,
            tokens: [x.contract_address],
            owner: PKP?.solanaAddress
          });
        } else {
          evmSVMBalances = await fetchEVMTokenBalances({
            network: x.network,
            tokens: [x.contract_address],
            owner: PKP?.additionalEthAddress
          });
        }
        const rawBalance = evmSVMBalances[0].rawBalance;
        if (fromAssetsFromState?.[0]?.contract_address == NATIVE_SOL || fromAssetsFromState?.[0]?.contract_address?.toLowerCase() == NATIVE_ADDRESS?.toLowerCase() || fromAssetsFromState?.[0]?.contract_address?.toLowerCase() == ZERO_ADDRESS?.toLowerCase() || fromAssetsFromState?.[0]?.contract_address == WRAPPED_SOL) {
          const assetPrice = await getTokenUSDPrice(fromAssetsFromState?.[0]?.network, fromAssetsFromState?.[0]?.contract_address == NATIVE_SOL || fromAssetsFromState?.[0]?.contract_address == WRAPPED_SOL ? WRAPPED_SOL : NATIVE_ADDRESS);
          const amountFor50cents = 1.1 / assetPrice;
          const amountFor5dollars = 5 / assetPrice;
          const amountFor10dollars = 10 / assetPrice;
          const decimals = V2Networks[fromAssetsFromState?.[0]?.network]?.decimals;
          const amountFor50centsDecimals = amountFor50cents * 10 ** decimals;
          const amountFor5dollarsDecimals = amountFor5dollars * 10 ** decimals;
          const amountFor10dollarsDecimals = amountFor10dollars * 10 ** decimals;

          // Need to reserve 5 dollars for DLN fees is solana is involved
          const solanaCrossChain = fromAssetsFromState?.[0]?.network == ChainId.SOLANA && toAssetsFromState?.[0]?.network != ChainId.SOLANA || toAssetsFromState?.[0]?.network == ChainId.SOLANA && fromAssetsFromState?.[0]?.network != ChainId.SOLANA;
          const amountToSub = fromAssetsFromState?.[0]?.network == 1 ? amountFor10dollarsDecimals : solanaCrossChain ? amountFor5dollarsDecimals : amountFor50centsDecimals;
          console.log('getting max amounts solanaCrossChain', solanaCrossChain, amountToSub);
          amount = Math.floor(parseFloat(rawBalance) - amountToSub)?.toString();
          console.log('new max amount adjusted for 50 cents', fromAssetsFromState?.[0]?.contract_address, amount, 'amountFor50cents', amountFor50cents, 'amountFor50centsDecimals', amountFor50centsDecimals, 'decomals', decimals, 'assetPrice', assetPrice, 'rawBalance', rawBalance);
        } else {
          amount = rawBalance?.toString();
        }
      }
      const amountUsd = x.amountToUse;
      return {
        address: x.contract_address,
        network: x.network,
        amount,
        amountUsd
      };
    }));
    const tokensOut = toAssets.map(x => {
      return {
        address: x.contract_address as Address,
        network: x.network,
        percentage: x.percentage
      };
    });
    const tokensToApprove = tokensIn?.map(x => {
      return {
        network: x.network,
        contract_address: x.address,
        amountToApprove: x.amount
      };
    });
    const {
      permitBatchDetails,
      permitSignatures
    } = await buildAllPermit2Details({
      tokensToApprove,
      ethersPKPWallet: await getEVMWallet()
    });
    console.log('new quote triggered from assets tokens in', tokensIn, 'tokensOut', tokensOut);
    const paramObj: IEnginePriceParams = {
      cloudWallet: PKP?.additionalEthAddress as Address,
      solanaAddress: PKP?.solanaAddress,
      tokensIn,
      tokensOut,
      fromSlippage: isNaN(fromConfig.slippage) ? 1 : fromConfig.slippage,
      fromGasMultiplier: fromConfig.gas,
      toSlippage: toConfig.slippage,
      toGasMultiplier: toConfig.gas,
      permitBatchDetails,
      permitSignatures,
      publicKeys: {
        payer: PKP?.solanaAddress
      },
      refreshToken: [...Array(10)].map(() => Math.random()?.toString(36)[2]).join('')
    };
    console.log('new quote triggered from assets setting quote params to', paramObj, validate(fromAssets, toAssets));
    setParams(paramObj);
  }
  const quoteStringInfo = quoteString(quoteData, toAssetPriceData);
  async function swapWrapper() {
    setSwapLoading(true);
    let id;
    if (!isMobile) {
      id = toast.loading('Submitting Order...', {});
    }
    const firstQuote = quoteData?.response?.[0];
    const transactions = firstQuote?.transactions;
    const isSponsored = firstQuote?.fees?.sponsorUsd;
    const isSolana = typeof transactions?.swap?.[0] == 'string';
    try {
      let orderType = 'SWAP';
      orderType += isSolana ? '_SOLANA' : '_EVM';
      orderType += isSponsored ? '_SPONSORED' : '';
      const modifiedAssets: (CovalentBalanceItem & {
        amountToUse: number;
        amountToUseStr: string;
      })[] = fromAssets.map((obj: CovalentBalanceItem) => {
        const amount = obj.amountToUse;
        return {
          ...obj,
          amountToUse: amount / obj.quote_rate,
          amountToUseStr: (amount / obj.quote_rate)?.toFixed(3)?.toString()
        };
      });
      const executionData: IExecuteViaClientResponse | {
        error: string;
      } = await executeOrderWrapper({
        params: firstQuote,
        sessionSigs: litSession,
        cloudWallet: PKP?.additionalEthAddress,
        signer: await getEVMWallet(),
        keypair: await getSolanaWallet(),
        pkp: {
          ...PKP
        },
        baseUrl: '/api/wrapper?target=',
        environment: ENVIRONMENT,
        orderType: orderType as OrderType,
        dbOrderParams: {
          tokenPrices: {
            tokensIn: modifiedAssets,
            tokensOut: toAssets
          },
          dbOrderType: 'MARKET_SWAP',
          toAssetPriceData: toAssetPriceData
        }
      });
      if ('error' in executionData && executionData.error) {
        console.error('Executing order swap error', executionData?.error);
        setSwapResult({
          error: executionData?.error
        });
        return;
      }
      try {
        const decodedResponse = ((executionData as IExecuteViaClientResponse)?.dbOrderHistory?.result as MultichainSwapResult)?.decodedResponse;
        const hashesAndNetwork = Object.keys(decodedResponse).map(key => {
          const hashNetworks = [];
          if (Array.isArray(decodedResponse[key])) {
            decodedResponse[key].forEach(item => {
              if (Array.isArray(item)) {
                item.forEach(subItem => {
                  if (subItem?.hash && subItem?.network) {
                    hashNetworks.push({
                      tokenName: `${fromAssets?.[0]?.contract_ticker_symbol} ➡️ ${toAssets?.[0]?.contract_ticker_symbol}`,
                      hash: subItem.hash,
                      network: subItem.network,
                      bundleId: subItem.bundleId
                    });
                  }
                });
              } else {
                if (item?.hash && item?.network) {
                  hashNetworks.push({
                    tokenName: `${fromAssets?.[0]?.contract_ticker_symbol} ➡️ ${toAssets?.[0]?.contract_ticker_symbol}`,
                    hash: item.hash,
                    network: item.network,
                    bundleId: item.bundleId
                  });
                }
              }
            });
          } else {
            if (decodedResponse[key]?.hash && decodedResponse[key]?.network) {
              hashNetworks.push({
                tokenName: `${fromAssets?.[0]?.contract_ticker_symbol} ➡️ ${toAssets?.[0]?.contract_ticker_symbol}`,
                hash: decodedResponse[key].hash,
                network: decodedResponse[key].network,
                bundleId: decodedResponse[key].bundleId
              });
            }
          }
          return hashNetworks;
        }).flat();
        console.log('Executing order - hashesAndNetwork', hashesAndNetwork);
        if (!hashesAndNetwork) {
          throw new Error('Error getting hashes and network from decodedResponse');
        }
        if (!isMobile) {
          toast.success('Order Submitted!', {
            id
          });
          toast.dismiss(id);
        }
        setStartTransactionConfirmationCheck(hashesAndNetwork);
      } catch (e) {
        console.error('hashesAndNetwork error', e);
        toast.error('We had an error submitting your order, please try again', {
          id
        });
      }
      updateIndexerAssets({
        fromAssets,
        toAssets,
        PKP,
        queryClient
      });
      const {
        dbOrder,
        result
      } = executionData as IExecuteViaClientResponse;
      if (result && result?.decodedResponse && result?.decodedResponse?.toString() === '') {
        console.error('Executing order swap error empty decodedResponse');
        setSwapResult({
          error: 'Error on execution data decodedResponse'
        });
        return;
      }
      if (result && result?.decodedResponse?.error || !result?.decodedResponse || result?.error) {
        await slackLogger({
          type: 'SWAP_ERROR',
          pkp: PKP,
          payload: {
            quote: quoteData,
            result: result
          }
        });
        setSwapResult({
          error: result?.decodedResponse.error || result?.error || 'Error Executing Order'
        });
        return;
      }
      if (result && result?.decodedResponse?.status === 'success') {
        setSwapResult({
          ...result,
          order_id: dbOrder?.id,
          time: parseInt(result?.executionTime?.toString()) / 1000
        });
      }
    } catch (e) {
      setSwapResult({
        error: e
      });
    } finally {
      setSwapLoading(false);
    }
  }
  const resetAssets = () => {
    clearQuoteData();
    setFromAssets([]);
    setToAssets([]);
    setSelectToAsset(false);
    setSelectFromAsset(false);
    setIsConfirmationDialogOpen(false);
    setSwapResult({
      error: '',
      time: 0
    });
  };
  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout;
    if (fromAssets.length > 0 && toAssets.length > 0) {
      if (!validate(fromAssets, toAssets).valid) {
        return;
      }
      if (isFirstRun) {
        console.log('Executing trigger 0 ');
        triggerQuote();
        setTimeUntilRefetchQuote(15);
        setIsFirstRun(false);
      } else {
        if (debounceTimeout) clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          triggerQuote();
          setTimeUntilRefetchQuote(15);
        }, 300);
      }
    }
    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [fromAssets?.[0]?.amountToUse, fromAssets?.[0]?.contract_address, toAssets?.[0]?.contract_address]);
  useInterval(() => {
    if (timeUntilRefetchQuote === 0) {
      console.log('Executing trigger 3');
      triggerQuote();
      setTimeUntilRefetchQuote(15);
    } else {
      setTimeUntilRefetchQuote(prev => prev - 1);
    }
  }, 1000, {
    enabled: !quoteLoad && !quoteFetching && !priceFetching && isConfirmationDialogOpen && validate(fromAssets, toAssets).valid && process.env.NEXT_PUBLIC_ENV !== 'LOCAL' && !(quoteStringInfo as {
      error: string;
      message: string;
    })?.message && urlHasTrade,
    immediate: false
  });
  const handleOnRefresh = async () => {
    setPriceFetching(true);
    setSwapResult({
      error: '',
      time: 0
    });
    await triggerQuote();
  };
  return <SwapContext.Provider value={{
    selectFromAsset,
    setSelectFromAsset,
    selectToAsset,
    setSelectToAsset,
    toAssets,
    setToAssets,
    fromAssets,
    setFromAssets,
    timeUntilRefetchQuote,
    setTimeUntilRefetchQuote,
    swapLoading,
    setSwapLoading,
    swapResult,
    setSwapResult,
    amount,
    setAmount,
    specialFixDecimals,
    quoteData,
    quoteLoad,
    quoteFetching,
    toAssetPriceData,
    activeTab,
    setActiveTab,
    quoteStringInfo,
    swapWrapper,
    fromConfig,
    setFromConfig,
    toConfig,
    setToConfig,
    priceFetching,
    isConfirmationDialogOpen,
    setIsConfirmationDialogOpen,
    quoteRefetch,
    setPriceFetching,
    resetAssets,
    triggerQuote,
    handleOnRefresh
  }} data-sentry-element="unknown" data-sentry-component="SwapProvider" data-sentry-source-file="SwapProvider.tsx">
      {children}
    </SwapContext.Provider>;
};
export const useSwap = () => {
  const swapValues = useContext(SwapContext);
  return swapValues;
};